import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Self, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, NgControl, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { APP_CONFIG } from '../../../app.config';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { makeValidatorChanges } from '../../../common/formValidationHelper';
import { ValidationService } from '../../../common/validation.service';
import { ValueListsService } from '../../../common/value-lists.service';
import { loadValueLists, ValueList } from '../../../common/valueListHelper';
import { AppConfig } from '../../../models/config/app-config';
import { verpflichtungserklaerungsgeberFieldDefinitions } from '../../../models/formControls/formFieldDefinitions';
import { ValueListItem } from '../../../models/rest/valuelist/valuelist';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';

@Component({
  selector: 'app-verpflichtungserklaerungsgeber',
  templateUrl: './verpflichtungserklaerungsgeber.component.html',
  styleUrls: ['./verpflichtungserklaerungsgeber.component.scss']
})
export class VerpflichtungserklaerungsgeberComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy, OnChanges {
  private subscriptions: Subscription = new Subscription();

  @Input() public andereValueChecked: boolean = false;

  @Input() public verpflichtungserklaerungsgeberListItems: ValueListItem[];

  private createForm(fb: FormBuilder): FormGroup {
    const controlsConfig: {} = {};

    // Zentrale definition der verpflichtungserklaerungsgeber-Felder da redundant
    verpflichtungserklaerungsgeberFieldDefinitions.forEach(field => {
      const validators: ValidatorFn[] = field.validatorParams
        ? this.validationService.getValidators(field.validatorPath, ...field.validatorParams)
        : this.validationService.getValidators(field.validatorPath);

      controlsConfig[field.controlName] = [null, validators];
    });

    return fb.group(controlsConfig);
  }

  constructor(fb: FormBuilder,
              private valueListsService: ValueListsService,
              private validationService: ValidationService,
              assistantPopoverService: AssistantPopoverService,
              @Inject(APP_CONFIG) public appConfig: AppConfig,
              @Optional() @Self() controlDir: NgControl) {
    super(controlDir, assistantPopoverService);
    this.form = this.wrapFormGroup(this.createForm(fb));
  }

  private readonly REQUIRED_FIELDS_FOR_PERSON : string[] = [
    'familienname',
    'vorname',
    'geburtsdatum',
    'geburtsort',
    'land',
    'geschlecht',
    'staatsangehoerigkeit'
  ];

  private readonly REQUIRED_FIELDS_FOR_ORGANIZATION : string[] = [
    'familienname',
    'vorname',
    'land'
  ];

  private readonly REQUIRED_FIELDS_FOR_DIPLOMAT : string[] = [
    'land'
  ];

  ngOnInit(): void {
    super.ngOnInit();

    const valueLists: ValueList[] = [
      { name: 'land', property: 'landListItems' },
      { name: 'staatsangehoerigkeit', property: 'staatsangehoerigkeitListItems' },
      { name: 'geschlecht', property: 'geschlechtListItems' },
      { name: 'organisation_register', property: 'registerListItems' },
      { name: 'organisation_wirkungsbereich', property: 'wirkungsbereichListItems' },
    ];

    loadValueLists(this.valueListsService, valueLists, this, this.subscriptions);

    // Sonderfallbehandlung händisch eingepflegt
    this.subscriptions.add(
      this.form.controls.art.valueChanges.subscribe(value => {
      this.enableOrDisableControls(value);
        makeValidatorChanges(this.form, this.validationService, value, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
      }));
    makeValidatorChanges(this.form, this.validationService, this.form.value.art, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.andereValueChecked) {
      this.handleArtInitialization();
    }
  }

  private handleArtInitialization() {
    const hasValue = (this.form.value.art !== null);
    if (hasValue) {
      return;
    }

    const hasDefaultValue = (this.verpflichtungserklaerungsgeberListItems !== undefined) && (0 < this.verpflichtungserklaerungsgeberListItems.length);
    if (hasDefaultValue) {
      const defaultValue = this.verpflichtungserklaerungsgeberListItems[0];
      this.form.controls.art.setValue(defaultValue.key);
      this.enableOrDisableControls(this.form.value.art);
      makeValidatorChanges(this.form, this.validationService, this.form.value.art, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
    }
  }

  onValidatorUpdate() {
    super.onValidatorUpdate();
    this.enableOrDisableControls(this.form.value.art);
    makeValidatorChanges(this.form, this.validationService, this.form.value.art, [this.REQUIRED_FIELDS_FOR_PERSON, this.REQUIRED_FIELDS_FOR_ORGANIZATION, this.REQUIRED_FIELDS_FOR_DIPLOMAT]);
  }

  private enableOrDisableControls(art: string) {
    switch (art) {
      case '1': { // Person
        this.disableOrgaControls();
        this.enableAnsprechpersonControls();
        break;
      }
      case '2': { // Organisation/Firma
        this.enableAnsprechpersonControls();
        this.enableOrgaControls();

        this.form.controls.abweichendNachname.disable();
        this.form.controls.abweichendVorname.disable();
        this.form.controls.andereNamen.disable();
        this.form.controls.fruehereNamen.disable();

        break;
      }

      case '3': { // Akkreditierter Diplomat
        this.disableOrgaControls();
        this.enableOrDisableControlWithTimeout(true, this.form.controls.orgaName);
        this.enableAnsprechpersonControls();
        this.form.controls.abweichendNachname.disable();
        this.form.controls.abweichendVorname.disable();
        this.form.controls.andereNamen.disable();
        this.form.controls.fruehereNamen.disable();
        break;
      }
    }
  }

  private enableAnsprechpersonControls() {
    this.form.controls.familienname.enable();
    this.form.controls.vorname.enable();
    this.form.controls.geschlecht.enable();
    this.form.controls.geburtsdatum.enable();
    this.form.controls.geburtsort.enable();
    this.form.controls.staatsangehoerigkeit.enable();
    this.form.controls.strasse.enable();
    this.form.controls.hausnummer.enable();
    this.form.controls.plz.enable();
    this.form.controls.ort.enable();
    this.form.controls.land.enable();
    this.form.controls.telefon.enable();
    this.form.controls.email.enable();
    this.form.controls.abweichendNachname.enable();
    this.form.controls.abweichendVorname.enable();
    this.form.controls.andereNamen.enable();
    this.form.controls.fruehereNamen.enable();
  }

  private enableOrgaControls() {
    this.enableOrDisableControlWithTimeout(true, this.form.controls.orgaName);
    this.form.controls.orgaSitz.enable();
    this.form.controls.orgaSitzLand.enable();
    this.form.controls.orgaAufgabe.enable();
    this.form.controls.orgaRegisterName.enable();
    this.form.controls.orgaRegisterOrt.enable();
    this.form.controls.orgaRegisternummer.enable();
  }

  private disableOrgaControls() {
    this.enableOrDisableControlWithTimeout(false, this.form.controls.orgaName);
    this.form.controls.orgaSitz.disable();
    this.form.controls.orgaSitzLand.disable();
    this.form.controls.orgaAufgabe.disable();
    this.form.controls.orgaRegisterName.disable();
    this.form.controls.orgaRegisterOrt.disable();
    this.form.controls.orgaRegisternummer.disable();
  }
}
